import { useState } from "react";
import ReactSelect, { components } from "react-select";
import config from "environment";
import { useDispatch, useSelector } from "react-redux";
import { iconPath } from "effno-core";
import { selectUser, setSelectedMenu, setSelectedMenuId, setUser } from "../../redux/features/appData";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import "./WorkspacePicker.css";
import ModalAddWorkspace from "./ModalAddWorkspace";
import { apiSetCurrentWorkspace } from "../../services/WorkspaceService";
import { MENUS } from "../config/menus";
import { useNavigate } from "react-router-dom";
import { ICONS } from "../shared/media";
import { ChevronsUpDown } from "lucide-react";

const allowNewWorkspace = config.allowNewWorkspace;
const defaultRoute = config.defaultRoute;

// note i use outlines here to hack around some weird CSS layering stuff
const selectStyles = {
  control: (baseStyles: any, state: any) => ({
    // backgroundColor: "var(--background-color-primary)",
    backgroundColor: "var(--workspace-background-color)",
    height: "40px",
    width: "100%",
    alignItems: "center",
    // outline: "var(--border-secondary)",
    outline: "none",
    borderRadius: "var(--border-radius)",
    margin: "0px",
    display: "flex",
  }),
  valueContainer: (baseStyles: any, state: any) => ({
    display: "flex",
    // backgroundColor: "var(--background-color-primary)",
    backgroundColor: "var(--workspace-background-color)",
    padding: "0px",
    margin: "0px",
    // color: "#FFF",
    color: "var(--text-color-primary)",
    fontSize: "14px",
    fontWeight: "800",
  }),
  singleValue: (baseStyles: any, state: any) => ({
    padding: "0px",
    borderRadius: "var(--border-radius)",
  }),
  menu: (baseStyles: any, state: any) => ({
    ...baseStyles,
    backgroundColor: "var(--background-color-primary)",
    borderRadius: "var(--border-radius)",
    outlineColor: "var(--menu-background-color-primary)",
    padding: "0px",
    marginTop: "8px",
  }),
  menuList: (baseStyles: any, state: any) => ({
    backgroundColor: "var(--background-color-primary)",
    padding: "0px",
    borderRadius: "var(--border-radius)",
  }),
  option: (baseStyles: any, state: any) => ({
    backgroundColor: state.isFocused
      ? "var(--menu-background-color-selected)"
      : "var(--menu-background-color-primary)",
    border: "1px solid none",
  }),
};

type WorkspacePickerProps = {
  selectedId: string;
}

export default function WorkspacePicker({ selectedId } : WorkspacePickerProps) {

  const { reloadAll} = useCoreDataLoader();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const storeUser = useSelector(selectUser);

  const [showNewWorkspace, setShowNewWorkspace] = useState(false);

  const CustomDropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <ChevronsUpDown size={16}/>
        {/* <img src={iconPath(ICONS.SWITCH_WORKSPACE, storeUser)} /> */}
      </components.DropdownIndicator>
    );
  };

  const pickerOptions = {
    IndicatorSeparator: () => null,
    DropdownIndicator: CustomDropdownIndicator,
  };

  let options : any[] = [];

  const workspacesCopy = (storeUser && storeUser.workspaces) ? [...storeUser.workspaces] : null;

  if (workspacesCopy) {
    workspacesCopy.sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );

    options = workspacesCopy.map((workspace, index) => {
      return {
        value: workspace.id,
        label: workspace.name,
        image: workspace.image,
        isSelected: false,
      };
    });
  }

  if (allowNewWorkspace) {
    // add the one to add
    options.push({
      value: "__new",
      label: "(add new workspace)",
      // image: "/icons/add_workspace.png",
      image: iconPath(ICONS.ADD_WORKSPACE, storeUser),
      isSelected: false,
    });
  }

  const defaultValue = workspacesCopy ? workspacesCopy.findIndex(
    (workspace) => workspace.id === selectedId
  ) : 0;

  const handleInputChange = (input: any, action: any) => {
    changeWorkspace(input.value);
  };

  const changeWorkspace = (workspaceId: string) => {
    if (workspaceId === "__new") {
      setShowNewWorkspace(true);
    } else {
      // set new workspace ID
      (async () => {
        setNewWorkspaceId(workspaceId);
        dispatch(setSelectedMenu(MENUS.HOME));
        dispatch(setSelectedMenuId(undefined));

        // const ct = await apiGetWorkspaceTargets(workspaceId);
        // console.log("[CollectionSidebar] changeWorkspace - reloading: ", workspaceId);
        reloadAll(workspaceId);

        navigate(defaultRoute);

      })();
    }
  };

  const setNewWorkspaceId = async (newWorkspaceId: string) => {
    if (!storeUser) {
      return;
    }
    
    const newUser = { ...storeUser, currentWorkspaceId: newWorkspaceId };
    await apiSetCurrentWorkspace(storeUser.id, newWorkspaceId);
    dispatch(setUser(newUser));
  };


  const closeNewWorkspaceModal = () => {
    setShowNewWorkspace(false);
  };

  const updateAfterNewWorkspace = () => {
    setShowNewWorkspace(false);
    dispatch(setSelectedMenu(MENUS.HOME));
    dispatch(setSelectedMenuId(undefined));

    navigate(defaultRoute);
  };

  return (
    <>
      <div className="workspace-picker-container">
        <ReactSelect
          styles={selectStyles}
          options={options}
          value={options[defaultValue]}
          components={pickerOptions}
          isSearchable={false}
          onChange={handleInputChange}
          formatOptionLabel={(option) => (
            <div className="workspace-picker-option">
              {option.value === "__new" && (
                <div className="workspace-picker-image">
                  <img
                    src={option.image}
                    style={{ height: "16px", width: "16px" }}
                    alt="workspace-image"
                  />
                </div>
              )}
              <div className="workspace-picker-name">{option.label}</div>
              {/* <div className="workspace-picker-unfold">
                            {option.isSelected ? <img className="icon-green-check"/> : <div style={{width: "24px"}}>&nbsp;</div>}
                        </div> */}
            </div>
          )}
        />
      </div>

      {showNewWorkspace && (
        <ModalAddWorkspace
          onClose={closeNewWorkspaceModal}
          isOpen={showNewWorkspace}
          onSuccess={updateAfterNewWorkspace}
        />
      )}
    </>
  );
}
